import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { 
  X, ChevronRight, ChevronLeft, Check, MapPin, ShoppingBag, 
  Wrench, Phone, MessageCircle, CreditCard, Settings, Star,
  Zap, Bell, Search, User, Home, Truck
} from 'lucide-react';

// Walkthrough steps for different user types
const CUSTOMER_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Jiffys Mobile!',
    description: 'Your one-stop app for roadside assistance and mobile mechanic services. Let\'s take a quick tour!',
    icon: Home,
    target: null, // No specific element
    position: 'center'
  },
  {
    id: 'request-service',
    title: 'Request Service',
    description: 'Tap here to request roadside assistance or mobile mechanic services. Choose your service type and location.',
    icon: Wrench,
    target: '[data-testid="request-service-btn"]',
    position: 'bottom'
  },
  {
    id: 'track-provider',
    title: 'Real-time Tracking',
    description: 'Once a provider accepts your job, track their location in real-time on the map with live ETA updates.',
    icon: MapPin,
    target: '[data-testid="tracking-map"]',
    position: 'top'
  },
  {
    id: 'marketplace',
    title: 'Marketplace',
    description: 'Buy and sell auto parts, tools, and accessories. Use Quick Add to import products from other sites!',
    icon: ShoppingBag,
    target: '[data-testid="nav-marketplace"]',
    position: 'bottom'
  },
  {
    id: 'emergency',
    title: 'Emergency Call',
    description: 'In an emergency? Tap the red banner to call our 24/7 hotline directly.',
    icon: Phone,
    target: '[data-testid="emergency-banner"]',
    position: 'bottom'
  },
  {
    id: 'ai-chat',
    title: 'AI Assistant',
    description: 'Need help? Our AI assistant can answer questions, help you choose services, and more.',
    icon: MessageCircle,
    target: '[data-testid="ai-chat-widget"]',
    position: 'left'
  },
  {
    id: 'notifications',
    title: 'Stay Updated',
    description: 'You\'ll receive notifications for job updates, provider arrival, and messages. Customize sound settings anytime.',
    icon: Bell,
    target: '[data-testid="notification-sound-toggle"]',
    position: 'bottom'
  },
  {
    id: 'settings',
    title: 'Customize Your Experience',
    description: 'Visit Settings to update your profile, change themes (dark/light), manage notifications, and more.',
    icon: Settings,
    target: '[data-testid="user-menu-trigger"]',
    position: 'bottom'
  }
];

const PROVIDER_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome, Provider!',
    description: 'You\'re now part of the Jiffys team. Let\'s show you how to manage jobs and maximize earnings.',
    icon: Truck,
    target: null,
    position: 'center'
  },
  {
    id: 'go-online',
    title: 'Go Online',
    description: 'Toggle this switch to start receiving job requests. When online, customers can see you on the map.',
    icon: Zap,
    target: '[data-testid="online-toggle"]',
    position: 'bottom'
  },
  {
    id: 'incoming-jobs',
    title: 'Job Requests',
    description: 'When a job comes in, you\'ll see a popup with details, pay, and location. Accept within 5 minutes!',
    icon: Bell,
    target: null,
    position: 'center'
  },
  {
    id: 'job-tabs',
    title: 'Manage Your Jobs',
    description: 'Track Assigned, Active, and Completed jobs here. View your earnings in the Earnings tab.',
    icon: Wrench,
    target: '[data-testid="provider-tabs"]',
    position: 'bottom'
  },
  {
    id: 'ai-negotiation',
    title: 'AI Price Negotiation',
    description: 'Our AI can help suggest fair prices based on market rates and job complexity. You always approve changes first.',
    icon: Star,
    target: null,
    position: 'center'
  },
  {
    id: 'earnings',
    title: 'Track Earnings',
    description: 'View your earnings, pending payouts, and request withdrawals from the Earnings tab.',
    icon: CreditCard,
    target: '[data-testid="earnings-tab"]',
    position: 'bottom'
  },
  {
    id: 'settings-provider',
    title: 'Your Settings',
    description: 'Update your skills, service area, bank details, and customize dark/light theme.',
    icon: Settings,
    target: '[data-testid="user-menu-trigger"]',
    position: 'bottom'
  }
];

/**
 * OnboardingWalkthrough - Interactive tooltip-based walkthrough
 */
export function OnboardingWalkthrough({ onComplete }) {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [tooltipPosition, setTooltipPosition] = useState({ top: '50%', left: '50%' });
  
  // Select steps based on user type
  const steps = user?.user_type === 'customer' ? CUSTOMER_STEPS : PROVIDER_STEPS;
  const step = steps[currentStep];
  const Icon = step?.icon || Home;
  
  // Calculate tooltip position based on target element
  useEffect(() => {
    if (!step?.target) {
      setTooltipPosition({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
      return;
    }
    
    const element = document.querySelector(step.target);
    if (!element) {
      setTooltipPosition({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
      return;
    }
    
    const rect = element.getBoundingClientRect();
    const scrollY = window.scrollY;
    
    let top, left, transform = '';
    
    switch (step.position) {
      case 'bottom':
        top = rect.bottom + scrollY + 12;
        left = rect.left + rect.width / 2;
        transform = 'translateX(-50%)';
        break;
      case 'top':
        top = rect.top + scrollY - 12;
        left = rect.left + rect.width / 2;
        transform = 'translate(-50%, -100%)';
        break;
      case 'left':
        top = rect.top + scrollY + rect.height / 2;
        left = rect.left - 12;
        transform = 'translate(-100%, -50%)';
        break;
      case 'right':
        top = rect.top + scrollY + rect.height / 2;
        left = rect.right + 12;
        transform = 'translateY(-50%)';
        break;
      default:
        top = '50%';
        left = '50%';
        transform = 'translate(-50%, -50%)';
    }
    
    setTooltipPosition({ top, left, transform });
    
    // Highlight the target element
    element.classList.add('onboarding-highlight');
    
    return () => {
      element.classList.remove('onboarding-highlight');
    };
  }, [step]);
  
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };
  
  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  const handleSkip = () => {
    handleComplete();
  };
  
  const handleComplete = () => {
    setIsVisible(false);
    // Mark onboarding as complete
    localStorage.setItem('onboarding_completed', 'true');
    if (onComplete) onComplete();
  };
  
  if (!isVisible) return null;
  
  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-[9998]"
        onClick={handleSkip}
      />
      
      {/* Tooltip */}
      <div 
        className="fixed z-[9999] w-[320px] max-w-[90vw]"
        style={{
          top: typeof tooltipPosition.top === 'number' ? `${tooltipPosition.top}px` : tooltipPosition.top,
          left: typeof tooltipPosition.left === 'number' ? `${tooltipPosition.left}px` : tooltipPosition.left,
          transform: tooltipPosition.transform
        }}
        data-testid="onboarding-tooltip"
      >
        <Card className="shadow-2xl border-2 border-blue-500 animate-in fade-in zoom-in duration-300">
          <CardContent className="p-4">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  <Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <Badge variant="outline" className="text-xs">
                  {currentStep + 1} of {steps.length}
                </Badge>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleSkip}
                className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Content */}
            <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-2">
              {step.title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              {step.description}
            </p>
            
            {/* Progress dots */}
            <div className="flex justify-center gap-1.5 mb-4">
              {steps.map((_, idx) => (
                <div 
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    idx === currentStep 
                      ? 'bg-blue-600' 
                      : idx < currentStep 
                      ? 'bg-blue-300' 
                      : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                />
              ))}
            </div>
            
            {/* Actions */}
            <div className="flex items-center justify-between">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleSkip}
                className="text-gray-500"
              >
                Skip Tour
              </Button>
              
              <div className="flex gap-2">
                {currentStep > 0 && (
                  <Button variant="outline" size="sm" onClick={handlePrev}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                )}
                <Button size="sm" onClick={handleNext} className="bg-blue-600 hover:bg-blue-700">
                  {currentStep === steps.length - 1 ? (
                    <>
                      <Check className="w-4 h-4 mr-1" />
                      Done
                    </>
                  ) : (
                    <>
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* CSS for highlight effect */}
      <style>{`
        .onboarding-highlight {
          position: relative;
          z-index: 9997 !important;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.5), 0 0 20px rgba(59, 130, 246, 0.3) !important;
          border-radius: 8px;
        }
      `}</style>
    </>
  );
}

/**
 * useOnboarding - Hook to manage onboarding state
 */
export function useOnboarding() {
  const { user } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  useEffect(() => {
    if (!user) return;
    
    // Check if onboarding was completed for this user or globally
    const globalCompleted = localStorage.getItem('onboarding_completed');
    const userOnboarded = localStorage.getItem(`onboarding_${user.user_id}`);
    
    // Don't show if either is set
    if (globalCompleted === 'true' || userOnboarded === 'true') {
      setShowOnboarding(false);
      return;
    }
    
    // Show onboarding for new users (first login)
    // Small delay to let the page render first
    const timer = setTimeout(() => {
      setShowOnboarding(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, [user]);
  
  const startOnboarding = () => setShowOnboarding(true);
  
  const completeOnboarding = () => {
    setShowOnboarding(false);
    // Set both flags to ensure persistence
    localStorage.setItem('onboarding_completed', 'true');
    if (user) {
      localStorage.setItem(`onboarding_${user.user_id}`, 'true');
    }
  };
  
  const resetOnboarding = () => {
    localStorage.removeItem('onboarding_completed');
    if (user) {
      localStorage.removeItem(`onboarding_${user.user_id}`);
    }
  };
  
  return {
    showOnboarding,
    startOnboarding,
    completeOnboarding,
    resetOnboarding
  };
}

export default OnboardingWalkthrough;
